import React, { useEffect } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { store, RootState } from './store'
import { setUser, setLoading } from './store/authSlice'
import authService from './services/authService'
import RootRoute from './components/RootRoute'
import Login from './pages/Login'
import Signup from './pages/Signup'
import AuthCallback from './pages/AuthCallback'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import CompanyRegistration from './pages/CompanyRegistration'
import CompanyRegistrationSuccess from './pages/CompanyRegistrationSuccess'
import Arena from './pages/Arena'
import Golf from './pages/Golf'
import GolfRoom from './pages/GolfRoom'
import GolfPlay from './pages/GolfPlay'
import Showcase from './pages/Showcase'
import Promo from './pages/Promo'
import AboutUs from './pages/AboutUs'
import Rankings from './pages/Rankings'
import Room from './pages/Room'
import Predictions from './pages/Predictions'
import FutureChampionPlay from './pages/FutureChampionPlay'
import Invitations from './pages/Invitations'
import Formula1Room from './pages/Formula1Room'
import Formula1Race from './pages/Formula1Race'
import Formula1Playing from './pages/Formula1Playing'
import ChessRoom from './pages/ChessRoom'
import ChessArenaRoom from './pages/ChessArenaRoom'
import ChessArenaGameReview from './pages/ChessArenaGameReview'
import ChessGame from './pages/ChessGame'
import { HIDE_IN_APP_CHESS } from './constants/chessProduct'
import PublicHub from './pages/PublicHub'
import PublicLeaderboard from './pages/PublicLeaderboard'
import PredictionBoard from './pages/PredictionBoard'
import PublicFootballPage from './pages/PublicFootballPage'
import News from './pages/News'
import Account from './pages/Account'
import PublicFormula1Page from './pages/PublicFormula1Page'
import PublicFormula1PlayPage from './pages/PublicFormula1PlayPage'
import AdminUpdateResults from './pages/AdminUpdateResults'
import AdminSocialPosters from './pages/AdminSocialPosters'
import AdminMissingPredictions from './pages/AdminMissingPredictions'
import AdminAgentAnalytics from './pages/AdminAgentAnalytics'
import AdminDashboard from './pages/AdminDashboard'
import AdminMatches from './pages/AdminMatches'
import AdminDbTables from './pages/AdminDbTables'
import AdminDbTable from './pages/AdminDbTable'
import PaymentsPage from './pages/PaymentsPage'
import InviteLanding from './pages/InviteLanding'
import ProtectedRoute from './components/ProtectedRoute'
import Navigation from './components/Navigation'
import ScrollToTop from './components/ScrollToTop'
import { ThemeProvider } from './hooks/use-theme'
import { LanguageProvider } from './hooks/use-i18n'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute - reuse cache when revisiting room
      refetchOnWindowFocus: false,
    },
  },
})

// Component to handle authentication restoration
const AuthInitializer: React.FC = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { user, token } = authService.restoreAuth()
        if (user && token) {
          // Optimistically restore from localStorage so the UI is instant,
          // then validate against the server (same pattern as mobileBrc).
          dispatch(setUser(user))
          try {
            const freshUser = await authService.getCurrentUser()
            // Keep localStorage in sync with the latest user data from server
            authService.setAuthData(freshUser, token)
            dispatch(setUser(freshUser))
          } catch {
            // Server validation failed (expired/revoked token) — log out cleanly
            authService.logout()
            dispatch(setUser(null))
          }
        }
      } catch (error) {
        console.error('Auth restoration failed:', error)
      } finally {
        dispatch(setLoading(false))
      }
    }

    initializeAuth()
  }, [dispatch])

  return null
}

const AppChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation()
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)
  const guestLanding = pathname === '/' && !isAuthenticated

  return (
    <div className="gradient-bg min-h-screen text-foreground overflow-x-clip">
      <Navigation />
      <div className={guestLanding ? undefined : 'pt-12 sm:pt-14 lg:pt-16'}>
        {children}
      </div>
    </div>
  )
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
        <Router>
          <ScrollToTop />
          <AuthInitializer />
          <AppChrome>
            <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/invite" element={<InviteLanding />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/arena" element={<Arena />} />
            <Route path="/golf" element={<Golf />} />
            <Route
              path="/golf/:roomId"
              element={
                <ProtectedRoute>
                  <GolfRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/golf/:roomId/play"
              element={
                <ProtectedRoute>
                  <GolfPlay />
                </ProtectedRoute>
              }
            />
            <Route path="/news" element={<News />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/promo" element={<Promo />} />
            <Route path="/promo/seq1" element={<Promo />} />
            <Route path="/demo/world-cup" element={<Navigate to="/showcase?football=1" replace />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/company-registration" element={<CompanyRegistration />} />
            <Route path="/company-registration/success" element={<CompanyRegistrationSuccess />} />
            <Route path="/public-arena/:roomId" element={HIDE_IN_APP_CHESS ? <Navigate to="/arena" replace /> : <ChessArenaRoom publicView />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/matches"
              element={
                <ProtectedRoute>
                  <AdminMatches />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminDbTable fixedTable="users" backTo="/admin" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/db"
              element={
                <ProtectedRoute>
                  <AdminDbTables />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/db/:table"
              element={
                <ProtectedRoute>
                  <AdminDbTable />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/update-results" 
              element={
                <ProtectedRoute>
                  <AdminUpdateResults />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/social-posters" 
              element={
                <ProtectedRoute>
                  <AdminSocialPosters />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/missing-predictions" 
              element={
                <ProtectedRoute>
                  <AdminMissingPredictions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/agent-analytics" 
              element={
                <ProtectedRoute>
                  <AdminAgentAnalytics />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/football" 
              element={
                <ProtectedRoute>
                  <PublicFootballPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/formula1" 
              element={
                <ProtectedRoute>
                  <PublicFormula1Page />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/formula1/play/:raceId" 
              element={
                <ProtectedRoute>
                  <PublicFormula1PlayPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/prediction-board" 
              element={
                <ProtectedRoute>
                  <PredictionBoard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/leaderboard" 
              element={
                <ProtectedRoute>
                  <PublicLeaderboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/:publicTournamentId/leaderboard" 
              element={
                <ProtectedRoute>
                  <PublicLeaderboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/public/:publicTournamentId" 
              element={
                <ProtectedRoute>
                  <PublicHub />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/account" 
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/invitations" 
              element={
                <ProtectedRoute>
                  <Invitations />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payments" 
              element={
                <ProtectedRoute>
                  <PaymentsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/room/:roomId" 
              element={
                <ProtectedRoute>
                  <Room />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/rankings/:roomId" 
              element={
                <ProtectedRoute>
                  <Rankings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/predictions/:roomId" 
              element={
                <ProtectedRoute>
                  <Predictions />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/room/:roomId/future-champion" 
              element={
                <ProtectedRoute>
                  <FutureChampionPlay />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/formula1/:roomId/playing"
              element={
                <ProtectedRoute>
                  <Formula1Playing />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/formula1/:roomId"
              element={
                <ProtectedRoute>
                  <Formula1Room />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/formula1-race/:roomId/:raceId" 
              element={
                <ProtectedRoute>
                  <Formula1Race />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/chess/:roomId"
              element={
                HIDE_IN_APP_CHESS ? (
                  <Navigate to="/arena" replace />
                ) : (
                  <ProtectedRoute>
                    <ChessRoom />
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/chess/:roomId/arena"
              element={
                HIDE_IN_APP_CHESS ? (
                  <Navigate to="/arena" replace />
                ) : (
                  <ProtectedRoute>
                    <ChessArenaRoom />
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/chess/:roomId/arena/review/:gameId"
              element={
                HIDE_IN_APP_CHESS ? (
                  <Navigate to="/arena" replace />
                ) : (
                  <ProtectedRoute>
                    <ChessArenaGameReview />
                  </ProtectedRoute>
                )
              }
            />
            <Route
              path="/chess-game/:gameId"
              element={
                HIDE_IN_APP_CHESS ? (
                  <Navigate to="/arena" replace />
                ) : (
                  <ProtectedRoute>
                    <ChessGame />
                  </ProtectedRoute>
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppChrome>
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
                backdropFilter: 'blur(12px)',
                borderRadius: 'var(--radius)',
              },
            }}
          />
        </Router>
        </LanguageProvider>
      </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  )
}

export default App
